import React, { ChangeEvent } from "react";
import { Row, Col, Select, Checkbox, Divider, Tabs, Input } from "antd";
import {
  BookingReportSaleFilters,
  BookingReportCsvFormat,
  EnsProductCarrierTid,
  EnsProductCategory,
  ForecastTableType,
  IBookingReportSaleFilters,
  ILiveforecastReportRequest,
} from "common/services/microservices/finance-client";
import { EnsSaleStatusTid } from "common/services/microservices/sales-client";
import { useState, useEffect } from "react";
import { CarrierCheckboxes } from "./CarrierCheckboxes";
import { ProductCheckboxes } from "./ProductCheckboxes";
import { StatusCheckboxes } from "./StatusCheckboxes";
import moment from "moment";
import { SaleDatePickers } from "./SaleDatePickers";

interface FullReportRequestBuilderProps {
  showForecastFilter: boolean;
  hideTableFilter?: boolean;
  onFiltersChange: (f: ILiveforecastReportRequest, ok: boolean) => void;
}

interface FilterBuilderProps {
  onFiltersChange: (f: IBookingReportSaleFilters, ok: boolean) => void;
}

function SaleFilterFilterBuilder(props: FilterBuilderProps) {
  const [carriers, setCarriers] = useState<EnsProductCarrierTid[]>([]);
  const [products, setProduct] = useState<EnsProductCategory[]>([]);
  const [issueStatus, setIssueStatus] = useState<EnsSaleStatusTid[]>([]);
  const [currentStatus, setCurrentStatus] = useState<EnsSaleStatusTid[]>([]);
  const [filterCarrier, setFilterCarrier] = useState<boolean>(false);
  const [filterProduct, setFilterProduct] = useState<boolean>(false);
  const [filterIssueStatus, setFilterIssueStatus] = useState<boolean>(false);
  const [filterCurrentStatus, setFilterCurrentStatus] = useState<boolean>(false);

  const [saleDateStart, setSaleDateStart] = useState<moment.Moment | undefined>(undefined);
  const [saleDateEnd, setSaleDateEnd] = useState<moment.Moment | undefined>(undefined);
  const [saleEffectiveDateStart, setSaleEffectiveDateStart] = useState<moment.Moment | undefined>(undefined);
  const [saleEffectiveDateEnd, setSaleEffectiveDateEnd] = useState<moment.Moment | undefined>(undefined);
  const [filterDates, setFilterDates] = useState<boolean>(false);

  const { onFiltersChange } = props;

  useEffect(() => {
    const filterObject: IBookingReportSaleFilters = {
      carriers: carriers,
      product_categories: products,
      issue_statuses: issueStatus,
      current_statuses: currentStatus,
      sale_date_start: saleDateStart?.toDate(),
      sale_date_end: saleDateEnd?.toDate(),
      sale_effective_date_start: saleEffectiveDateStart?.toDate(),
      sale_effective_date_end: saleEffectiveDateEnd?.toDate(),
    };

    const goodCarrier = filterCarrier ? carriers.length > 0 : true;
    const goodProduct = filterProduct ? products.length > 0 : true;
    const goodIssueStatus = filterIssueStatus ? issueStatus.length > 0 : true;
    const goodCurrentStatus = filterCurrentStatus ? currentStatus.length > 0 : true;
    const goodDates = filterDates
      ? !!saleDateStart || !!saleDateEnd || !!saleEffectiveDateStart || !!saleEffectiveDateEnd
      : true;

    const goodFilters = goodCarrier && goodProduct && goodIssueStatus && goodCurrentStatus && goodDates;

    onFiltersChange(new BookingReportSaleFilters(filterObject), goodFilters);
  }, [
    filterCarrier,
    filterProduct,
    filterIssueStatus,
    filterCurrentStatus,
    filterDates,
    carriers,
    products,
    issueStatus,
    currentStatus,
    saleDateStart,
    saleDateEnd,
    saleEffectiveDateStart,
    saleEffectiveDateEnd,
  ]);

  return (
    <>
      <Col span={5} style={{ display: "flex", flexDirection: "column", alignSelf: "start" }}>
        <Checkbox
          value={filterCarrier}
          onChange={(f) => {
            const checked = f.target.checked;
            if (!checked) {
              setCarriers([]);
            }
            setFilterCarrier(f.target.checked);
          }}
        >
          Filter Carriers
        </Checkbox>
        <Divider />
        {filterCarrier && (
          <CarrierCheckboxes
            disabled={!filterCarrier}
            selectedCarriers={carriers}
            onCarriersSelected={(s) => setCarriers(s)}
          />
        )}
      </Col>
      <Col span={5} style={{ display: "flex", flexDirection: "column", alignSelf: "start" }}>
        <Checkbox
          value={filterProduct}
          onChange={(f) => {
            const checked = f.target.checked;
            if (!checked) {
              setProduct([]);
            }
            setFilterProduct(f.target.checked);
          }}
        >
          Filter Product Categories
        </Checkbox>
        <Divider />
        {filterProduct && (
          <ProductCheckboxes
            disabled={!filterProduct}
            selectedProductCategories={products}
            onProductCategoriesSelected={(s) => setProduct(s)}
          />
        )}
      </Col>
      <Col span={5} style={{ display: "flex", flexDirection: "column", alignSelf: "start" }}>
        <Checkbox
          value={filterIssueStatus}
          onChange={(f) => {
            const checked = f.target.checked;
            setFilterIssueStatus(checked);
            if (!checked) {
              setIssueStatus([]);
            }
          }}
        >
          Filter Issue Status
        </Checkbox>
        <Divider />
        {filterIssueStatus && (
          <StatusCheckboxes
            disabled={!filterIssueStatus}
            selectedStatuses={issueStatus}
            onStatusesSelected={(s) => setIssueStatus(s)}
          />
        )}
      </Col>
      <Col span={5} style={{ display: "flex", flexDirection: "column", alignSelf: "start" }}>
        <Checkbox
          value={filterCurrentStatus}
          onChange={(f) => {
            const checked = f.target.checked;
            setFilterCurrentStatus(checked);
            if (!checked) {
              setCurrentStatus([]);
            }
          }}
        >
          Filter Current Status
        </Checkbox>
        <Divider />
        {filterCurrentStatus && (
          <StatusCheckboxes
            disabled={!filterCurrentStatus}
            selectedStatuses={currentStatus}
            onStatusesSelected={(s) => setCurrentStatus(s)}
          />
        )}
      </Col>
      <Col span={4} style={{ display: "flex", flexDirection: "column", alignSelf: "start" }}>
        <Checkbox
          value={filterDates}
          onChange={(f) => {
            const checked = f.target.checked;
            setFilterDates(checked);
            if (!checked) {
              setSaleDateStart(undefined);
              setSaleDateEnd(undefined);
              setSaleEffectiveDateStart(undefined);
              setSaleEffectiveDateEnd(undefined);
            }
          }}
        >
          Filter Sale Dates
        </Checkbox>
        <Divider />
        {filterDates && (
          <SaleDatePickers
            filterDates={filterDates}
            saleDateStart={saleDateStart}
            saleDateEnd={saleDateEnd}
            saleEffectiveDateStart={saleEffectiveDateStart}
            saleEffectiveDateEnd={saleEffectiveDateEnd}
            setSaleDateStart={setSaleDateStart}
            setSaleDateEnd={setSaleDateEnd}
            setSaleEffectiveDateStart={setSaleEffectiveDateStart}
            setSaleEffectiveDateEnd={setSaleEffectiveDateEnd}
          />
        )}
      </Col>
    </>
  );
}

function SaleIdFilterBuilder(props: FilterBuilderProps) {
  const { onFiltersChange } = props;

  const [saleIds, setSaleIds] = useState<number[]>([]);
  const [badInputs, setBadInputs] = useState<string[]>([]);

  function onTextAreaChange(event: ChangeEvent<HTMLTextAreaElement>) {
    const splitLines = event.target.value.split("\n").filter((s) => s.length > 0);
    const splitLineNumbers = splitLines.map((s) => ({ number: parseInt(s), string: s }));
    const goodInputs = splitLineNumbers.filter((i) => !isNaN(i.number));
    const badInputs = splitLineNumbers.filter((i) => isNaN(i.number));
    setSaleIds(goodInputs.map((i) => i.number));
    setBadInputs(badInputs.map((i) => i.string));
  }

  useEffect(() => {
    if (badInputs.length > 0) {
      onFiltersChange({}, false);
    } else if (saleIds.length > 0) {
      onFiltersChange({ sale_ids: saleIds }, true);
    } else {
      onFiltersChange({}, false);
    }
  }, [saleIds, badInputs]);

  return (
    <Col span={10}>
      <Row align="middle">
        <p>
          <b>{saleIds.length}</b> Sale{saleIds.length == 1 ? "" : "s"} Entered
        </p>
      </Row>
      {badInputs.length > 0 && (
        <>
          <Row align="middle">
            <b>
              {badInputs.length} Bad Input{badInputs.length == 1 ? "" : "s"} Entered. Correct before submitting:
            </b>
          </Row>
          <Row>
            <ul>
              {badInputs.map((i, key) => (
                <li key={key}>{i}</li>
              ))}
            </ul>
          </Row>
        </>
      )}
      <Row align="middle">
        <Input.TextArea rows={10} placeholder="Enter Sale IDs, one per line" onChange={onTextAreaChange} />
      </Row>
    </Col>
  );
}

type TabKey = "filters" | "saleIds";

export function FullReportRequestBuilder(props: FullReportRequestBuilderProps) {
  const { showForecastFilter, hideTableFilter = false, onFiltersChange } = props;

  const [forcastNumber, setForecastNumer] = useState<number>(1);
  const [reportType, setReportType] = useState<BookingReportCsvFormat>(BookingReportCsvFormat.AdminBaseSeparate);
  const [forcastTable, setForcastTable] = useState<ForecastTableType>(ForecastTableType.TripleSeven);
  const [filters, setFilters] = useState<IBookingReportSaleFilters>({});
  const [filtersOk, setFiltersOk] = useState<boolean>(true);

  useEffect(() => {
    onFiltersChange(
      {
        report_type: reportType,
        filters: new BookingReportSaleFilters(filters),
        forecast_type_tid: forcastNumber,
        forecast_table: forcastTable,
      },
      filtersOk
    );
  }, [filters, filtersOk, reportType, forcastNumber, forcastTable]);

  function onTabChange(key: TabKey) {
    const defaultFilterOkValue: Record<TabKey, boolean> = {
      filters: true,
      saleIds: false,
    };

    setFilters({});
    setFiltersOk(defaultFilterOkValue[key] ?? false);
  }

  return (
    <>
      <Row align="middle" justify="center" style={{ paddingBottom: "20px" }}>
        {!hideTableFilter && (
          <Col span={6}>
            <Col>Table</Col>
            <Select<number>
              style={{ width: "100%" }}
              value={forcastTable}
              options={[
                { value: ForecastTableType.TripleSeven, label: "777" },
                { value: ForecastTableType.Discounted, label: "777 Discounted" },
                { value: ForecastTableType.Uhas, label: "Uhas" },
              ]}
              onChange={(value) => setForcastTable(value)}
            />
          </Col>
        )}
        {showForecastFilter && (
          <Col span={6}>
            <Col>Forecast</Col>
            <Select<number>
              style={{ width: "100%" }}
              placeholder={"Forecast..."}
              value={forcastNumber}
              options={[
                { value: 1 },
                { value: 2 },
                { value: 3 },
                { value: 4 },
                { value: 5 },
                { value: 6 },
                { value: 7 },
                { value: 8 },
                { value: 9 },
              ]}
              onChange={(value) => setForecastNumer(value)}
            />
          </Col>
        )}
        <Col span={6}>
          <Col>Report Format</Col>
          <Select<number>
            style={{ width: "100%" }}
            value={reportType}
            options={[
              { value: BookingReportCsvFormat.AdminBaseSeparate, label: "Admin/Base Separate" },
              { value: BookingReportCsvFormat.AdminBaseTogether, label: "Admin/Base Together" },
            ]}
            onChange={(value) => setReportType(value)}
          />
        </Col>
      </Row>
      <Divider />
      <Tabs centered onChange={(key) => onTabChange(key as TabKey)}>
        <Tabs.TabPane tab="By Sale Filters" key="filters">
          <Row align="middle" justify="center" style={{ paddingBottom: "20px" }}>
            <SaleFilterFilterBuilder
              onFiltersChange={(filters, ok) => {
                setFilters(filters);
                setFiltersOk(ok);
              }}
            />
          </Row>
        </Tabs.TabPane>
        <Tabs.TabPane tab="By Sale ID List" key="saleIds">
          <Row align="middle" justify="center" style={{ paddingBottom: "20px" }}>
            <SaleIdFilterBuilder
              onFiltersChange={(filters, ok) => {
                setFilters(filters);
                setFiltersOk(ok);
              }}
            />
          </Row>
        </Tabs.TabPane>
      </Tabs>
    </>
  );
}
